import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import type { NegotiationSort } from '../components/negotiations-filter-tabs'
import {
  type NegotiationsAdminV2Query,
  negotiationsAdminV2Service
} from '../services/negotiations-admin-v2.service'

type UseNegotiationsAdminV2QueryArgs = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: NegotiationSort
  search?: string
  pageSize: number
}

/**
 * El tablero de negociaciones v2, con navegación por PILA de cursores (ADR 0019).
 *
 * Espeja los hooks de la ola —clientes, sociedades, proyectos, workflows— y no
 * inventa nada: la pila guarda el cursor `after` de cada página, `goNext` apila
 * el `nextCursor`, `goPrev` desapila, y cambiar filtros, orden, búsqueda o
 * tamaño la reinicia.
 *
 * **No hay «ir a la página 7», y es a propósito.** El ADR 0019 lo dice con todas
 * las letras: «no se preservan números de página por compatibilidad visual». El
 * cursor es keyset, no offset. Lo que se gana a cambio importa más en este
 * tablero que en cualquier otro listado del back-office: con offset, dar de alta
 * una negociación mientras alguien navega **corre la ventana** y le repite o le
 * saltea filas, y acá las filas son expedientes que alguien está trabajando.
 */
export function useNegotiationsAdminV2Query({
  filters,
  sort,
  search,
  pageSize
}: UseNegotiationsAdminV2QueryArgs) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''

  const resetKey = JSON.stringify({ filters, sort, search, pageSize })
  const [cursorState, setCursorState] = useState<{
    key: string
    stack: (string | undefined)[]
  }>({ key: resetKey, stack: [undefined] })
  if (cursorState.key !== resetKey) {
    setCursorState({ key: resetKey, stack: [undefined] })
  }
  const after = cursorState.stack[cursorState.stack.length - 1]

  const listQuery: NegotiationsAdminV2Query = { filters, sort, search, pageSize, after }
  const query = useQuery({
    queryKey: queryKeys.negotiations.adminListV2({ filters, sort, search, pageSize, after }),
    queryFn: async () => await negotiationsAdminV2Service.list(accessToken, listQuery),
    enabled: Boolean(accessToken),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    // Un 403/500 es determinista: reintentarlo sólo retrasa el aviso al usuario.
    retry: false
  })

  const nextCursor = query.data?.pagination?.nextCursor
  const goNext = useCallback(() => {
    if (!nextCursor) return
    setCursorState((state) => ({ ...state, stack: [...state.stack, nextCursor] }))
  }, [nextCursor])

  const goPrev = useCallback(() => {
    setCursorState((state) =>
      state.stack.length > 1 ? { ...state, stack: state.stack.slice(0, -1) } : state
    )
  }, [])

  return {
    query,
    rows: query.data?.data ?? [],
    page: cursorState.stack.length,
    canPrev: cursorState.stack.length > 1,
    canNext: query.data?.pagination?.hasMore ?? false,
    goNext,
    goPrev
  }
}

/**
 * Los conteos del tablero: total, por estado, por tipo de estado, vencidas y
 * rechazadas. Mismos filtros que el listado, sin paginar.
 *
 * `byState` viene indexado por `sta_`, que es **la misma llave con la que el
 * chip filtra**. Ese cruce es todo el punto de la tajada: en v1 el chip mandaba
 * el uuid legacy y los conteos venían con esa misma llave, así que migrar una
 * sola de las dos puntas dejaba todos los contadores en cero sin dar error.
 */
export function useNegotiationsAdminV2Summary(args: {
  filters?: Record<string, unknown>
  search?: string
}) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  return useQuery({
    queryKey: queryKeys.negotiations.summaryV2(args),
    queryFn: async () => await negotiationsAdminV2Service.summary(accessToken, args),
    enabled: Boolean(accessToken),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false
  })
}

/**
 * Los estados que este usuario puede leer, para los chips del tablero.
 *
 * Reemplaza a `useMyTransitionsQuery`, que llamaba al verb path v1 y **se comía
 * el error con un `catch` que devolvía `[]`**: si el endpoint fallaba, el
 * tablero se quedaba sin un solo chip y eso se lee igual que «no tengo trabajo».
 * Acá el error sube y la pantalla decide qué mostrar, que es lo que el ADR 0031
 * pide para las queries.
 */
export function useMyStateTransitionsV2Query() {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  return useQuery({
    queryKey: queryKeys.states.myTransitionsV2(),
    queryFn: async () => await negotiationsAdminV2Service.myStateTransitions(accessToken),
    enabled: Boolean(accessToken),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false
  })
}

/**
 * Las últimas negociaciones, para el dashboard del back-office.
 *
 * NO es un endpoint nuevo: es el listado de siempre con `sort=createdAt:desc` y
 * una página chica. `GET /negotiations/dashboard/recent-negotiations` existía en
 * v1 sólo porque el listado legacy no sabía ordenar ni acotar así — con el
 * contrato del ADR 0019 la pregunta «las últimas cinco» ya se puede hacer, y una
 * ruta propia para eso sería superficie que hay que mantener sin ganar nada.
 *
 * Se ordena por `createdAt` y no por «lo último que se movió», que sería la
 * pregunta natural: ese dato vive en `currentState.stateAt`, un STRING que
 * ordena por día de la semana (ver el allowlist). Es la misma razón por la que el
 * tablero abre por `updatedAt`.
 */
export function useRecentNegotiationsV2Query(limit = 5) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  return useQuery({
    queryKey: queryKeys.negotiations.recentV2(limit),
    queryFn: async () =>
      await negotiationsAdminV2Service.list(accessToken, {
        sort: 'createdAt:desc',
        pageSize: limit
      }),
    enabled: Boolean(accessToken),
    refetchOnWindowFocus: false,
    retry: false
  })
}

/**
 * Las negociaciones de UN cliente. Sucede a `GET /negotiations/by-client/:id`.
 *
 * No es un endpoint nuevo: es el listado con `filters[ownerId]`, que el allowlist
 * declara sobre el arreglo de los `cli_` de todos los propietarios. Un expediente
 * con tres propietarios aparece en la ficha de los tres, igual que en v1.
 *
 * Ordena por `code:desc` —el identificador con el que el negocio habla de una
 * negociación— y no por «lo último que se movió», que sería `stateAt`: ese campo
 * es un string y ordenarlo compara días de la semana (ver el allowlist).
 *
 * `pageSize` es alto a propósito: estas tres pantallas son fichas de cliente, no
 * tableros, y muestran la lista entera sin paginar. Un cliente con más de cien
 * negociaciones no existe en los datos —el máximo medido es de un dígito—, y si
 * apareciera, cortar en cien es preferible a colgar la ficha.
 */
export function useNegotiationsByClientV2Query({
  clientId,
  enabled = true
}: {
  clientId: string | undefined
  enabled?: boolean
}) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  return useQuery({
    queryKey: queryKeys.negotiations.byClientV2(clientId ?? null),
    queryFn: async () =>
      await negotiationsAdminV2Service.list(accessToken, {
        filters: { ownerId: { $eq: clientId } },
        sort: 'code:desc',
        pageSize: 100
      }),
    enabled: enabled && Boolean(accessToken) && Boolean(clientId),
    refetchOnWindowFocus: false,
    retry: false
  })
}

/**
 * El detalle de un expediente por `neg_`. Sucede a `useNegotiationDetailQuery`, que pegaba a
 * `GET /negotiations/:id` de v1.
 *
 * `staleTime` de 30 s, igual que el hook al que reemplaza: el panel se abre y se cierra mucho
 * sobre la misma fila, y volver a pedir en cada apertura no cambia lo que se ve.
 */
export function useNegotiationDetailV2Query(id: string | null | undefined) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  return useQuery({
    queryKey: queryKeys.negotiations.detailV2(id ?? null),
    queryFn: async () => await negotiationsAdminV2Service.detail(accessToken, id ?? ''),
    enabled: Boolean(accessToken) && Boolean(id),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false
  })
}
